import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type NumericService from '@/services/numericService'
import type PhoneService from '@/services/phoneService'
import type ContactService from '@/services/domain/contact/contactService'
import type ContactListService from '@/services/domain/contactList/contactListService'
import type { SegmentsService } from '@/services/domain/contact/segments/segmentsService'
import type ContactListFolderService from '@/services/domain/contactGrouping/contactListFolderService'
import type Contact from '@/data/models/domain/Contact'
import type TranslateService from '@/services/translateService'
import type { Optional } from '@/types'

@injectable()
export class RecipientDataMapperService {
  constructor(
    @inject(SERVICE_TYPES.NumericService) protected readonly numericService: NumericService,
    @inject(SERVICE_TYPES.PhoneService) protected readonly phoneService: PhoneService,
    @inject(SERVICE_TYPES.ContactService) protected readonly contactService: ContactService,
    @inject(SERVICE_TYPES.ContactListService) protected readonly contactListService: ContactListService,
    @inject(SERVICE_TYPES.SegmentsService) protected readonly segmentsService: SegmentsService,
    @inject(SERVICE_TYPES.ContactListFolderService)
    protected readonly contactListFolderService: ContactListFolderService,
    @inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService,
  ) {}

  public computeContactHint(contact: Pick<Contact, 'phone' | 'fullName'>): Optional<string> {
    if (!contact?.phone) {
      return undefined
    }
    const label = contact.fullName?.trim() ?? ''
    if (label === contact.phone) {
      return undefined
    }
    return this.phoneService.preparePhoneForDisplay(contact.phone)
  }

  public getContactLabel(entityId: string | number): Optional<string> {
    const contact = this.contactService.findEntityByIdOrNull(String(entityId))
    return contact?.fullName?.trim()
  }

  public getContactHint(entityId: string | number): Optional<string> {
    const contact = this.contactService.findEntityByIdOrNull(String(entityId))
    if (!contact) {
      return undefined
    }
    return this.computeContactHint(contact)
  }

  public getContactEmailLabel(entityId: string | number): Optional<string> {
    const contact = this.contactService.findEntityByIdOrNull(String(entityId))
    return contact?.fullName?.trim()
  }

  public getContactEmailHint(entityId: string | number): string | undefined {
    const contact = this.contactService.findEntityByIdOrNull(String(entityId))
    return contact?.email ?? undefined
  }

  public formatMembersCount(count: number | undefined): string | undefined {
    if (typeof count === 'undefined') {
      return undefined
    }

    return this.numericService.formatInteger(count)
  }

  public getListLabel(entityId: string | number): Optional<string> {
    const list = this.contactListService.findEntityByIdOrNull(String(entityId))
    return list?.name
  }

  public getListHint(entityId: string | number): Optional<string> {
    const list = this.contactListService.findEntityByIdOrNull(String(entityId))
    return this.formatMembersCount(list?.membersCount)
  }

  public getFolderLabel(entityId: string | number): Optional<string> {
    const folder = this.contactListFolderService.findEntityByIdOrNull(String(entityId))
    return folder?.name
  }

  public getFolderHint(entityId: string | number): Optional<string> {
    const folder = this.contactListFolderService.findEntityByIdOrNull(String(entityId))
    return this.formatListsCount(folder?.listsCount)
  }

  public formatListsCount(count: number | undefined): string | undefined {
    if (typeof count === 'undefined') {
      return undefined
    }

    return this.translateService.tc('entities.contactList.listsCountText', count, {
      countText: this.numericService.formatInteger(count),
    })
  }

  public getFilteredViewLabel(entityId: string | number): Optional<string> {
    const segment = this.segmentsService.findEntityByIdOrNull(String(entityId))
    return segment?.name
  }

  public getFilteredViewHint(entityId: string | number): Optional<string> {
    const segment = this.segmentsService.findEntityByIdOrNull(String(entityId))
    return this.formatMembersCount(segment?.count)
  }

  public getPhoneLabel(phoneNumber: string): string {
    return this.phoneService.preparePhoneForDisplay(phoneNumber)
  }

  public getEmailLabel(email: string): string {
    return email
  }
}
